import React from 'react';
import { motion } from 'framer-motion';
import { Crown, Pencil } from 'lucide-react';
import { Avatar } from '../UI/Avatar';
import { PERMISSION_LABELS, type TeamResponse } from '../../api/eventTeam';
import { clayTapScale } from '../../constants/animations';
import type { EventManager, EventManagerPermissions } from '../../types';
import styles from './ManagerList.module.css';

interface ManagerListProps {
  creator: TeamResponse['creator'];
  managers: EventManager[];
  canEdit: boolean;
  onEdit: (manager: EventManager) => void;
  emptyHint?: string;
}

function summarizePermissions(perms: EventManagerPermissions): string {
  const granted = (Object.entries(perms) as [keyof EventManagerPermissions, boolean][])
    .filter(([, v]) => v)
    .map(([k]) => PERMISSION_LABELS[k]);

  if (granted.length === 0) return 'View-only access';
  if (granted.length <= 2) return granted.join(' · ');
  return `${granted[0]} · ${granted[1]} · +${granted.length - 2} more`;
}

function statusLabel(status: EventManager['status']): string | null {
  if (status === 'pending') return 'Email sent';
  if (status === 'invited') return 'Awaiting acceptance';
  return null;
}

export function ManagerList({ creator, managers, canEdit, onEdit, emptyHint }: ManagerListProps) {
  return (
    <ul className={styles['list']}>
      {/* Creator row — always present, never editable */}
      <li className={[styles['row'], styles['row-creator']].join(' ')}>
        <div className={styles['identity']}>
          <Avatar name={creator.name} src={creator.picture ?? undefined} size="md" />
          <div className={styles['identity-text']}>
            <div className={styles['identity-name']}>
              {creator.name}
              <span className={[styles['identity-badge'], styles['identity-badge-creator']].join(' ')}>
                <Crown className={styles['badge-icon']} />
                Creator
              </span>
            </div>
            <div className={styles['identity-email']}>{creator.email}</div>
            <div className={styles['identity-perms']}>Full access · cannot be edited</div>
          </div>
        </div>
      </li>

      {managers.length === 0 ? (
        <li className={styles['empty']}>
          {emptyHint ?? 'No managers yet.'}
        </li>
      ) : (
        managers.map((manager) => {
          const userObj = typeof manager.userId === 'object' ? manager.userId : null;
          const displayName = userObj?.name ?? manager.email;
          const status = statusLabel(manager.status);

          return (
            <li key={manager._id} className={styles['row']}>
              <div className={styles['identity']}>
                <Avatar name={displayName} src={userObj?.picture ?? undefined} size="md" />
                <div className={styles['identity-text']}>
                  <div className={styles['identity-name']}>
                    {displayName}
                    {status && <span className={styles['identity-badge']}>{status}</span>}
                  </div>
                  <div className={styles['identity-email']}>{manager.email}</div>
                  <div className={styles['identity-perms']}>
                    {summarizePermissions(manager.permissions)}
                  </div>
                </div>
              </div>

              {canEdit && (
                <motion.button
                  type="button"
                  onClick={() => onEdit(manager)}
                  className={styles['edit-btn']}
                  whileTap={clayTapScale}
                  aria-label={`Edit ${displayName}`}
                >
                  <Pencil className={styles['edit-icon']} />
                </motion.button>
              )}
            </li>
          );
        })
      )}
    </ul>
  );
}
